import { useState } from 'react';
import { Link } from 'react-router';

import {
  CycleStage,
  SubmissionStatus,
  type CycleStandingResponse,
  type SubmissionResponse,
} from '../../api';
import { FormError, Panel, useNotifications } from '../../components';
import { formatDate, formatDateTime } from '../../format';
import { staffSubmissionConsolidatedPath, staffSubmissionGroupPath } from '../../routes/paths';
import { saveAsAttachment } from '../../utils/download';
import {
  useMarkSubmissionFailed,
  useMarkSubmissionSent,
  usePrepareSubmission,
  useSnapshotFile,
} from '../queries';
import {
  CHANNEL_LABELS,
  SUBMISSION_COPY,
  STAGE_LABELS,
  isOpenAttempt,
  proofOf,
} from './submissionCycle';
import { MarkFailedForm, MarkSentForm } from './SubmissionMarkerForms';

/**
 * One contract's month, and the one thing there is to do about it next.
 *
 * The panel is a state machine with four states and it shows exactly one of
 * them, because "what do I do about this contract now?" has one answer at a
 * time and a screen offering all four would be asking the analyst to work it
 * out:
 *
 *  - **open** — nobody has signed the cutoff off, so there is nothing to file.
 *    The panel says so and sends them to the review queue rather than offering
 *    a button that would be a `409`.
 *  - **approved** (or **failed**, which is approved with a refusal behind it) —
 *    the file is frozen and can be packaged.
 *  - **prepared** — the file is out of the building. Two things can have
 *    happened to it and both are on offer: it was filed, or it was refused.
 *  - **sent** — the month is closed on our side, and what is shown is the
 *    evidence.
 *
 * **Nothing here transmits anything.** Every button records what a person did
 * with the file, which is stated on the screen rather than only in the API
 * documentation, because an analyst pressing "marcar como enviado" should not
 * be able to believe the system sent it.
 */
export function SubmissionCyclePanel({ standing }: { standing: CycleStandingResponse }) {
  const { notify } = useNotifications();
  const prepare = usePrepareSubmission();
  const markSent = useMarkSubmissionSent();
  const markFailed = useMarkSubmissionFailed();
  const [marking, setMarking] = useState<'sent' | 'failed' | null>(null);

  const submission = standing.submission ?? null;
  const target = {
    contractId: standing.contract_id,
    companyId: standing.company_id,
    cycleMonth: standing.cycle_month,
  };
  // A contract filed inside its company's single file is prepared and marked
  // only there. Its own month stays readable here, and the panel says where
  // the work happens rather than offering buttons the server would refuse.
  const filedTogether = standing.consolidated;
  const canPrepare =
    !filedTogether &&
    (standing.stage === CycleStage.APPROVED || standing.stage === CycleStage.FAILED);

  return (
    <Panel
      title={SUBMISSION_COPY.standingTitle}
      description={`${standing.company_name} · ${standing.insurer_name} — ${STAGE_LABELS[standing.stage]}`}
    >
      <dl className="definitions">
        <div>
          <dt>Ciclo</dt>
          <dd>{standing.cycle_month}</dd>
        </div>
        <div>
          <dt>Data de corte</dt>
          <dd>{formatDate(standing.cutoff_date)}</dd>
        </div>
        <div>
          <dt>Situação</dt>
          <dd>{STAGE_LABELS[standing.stage]}</dd>
        </div>
        {standing.approval != null && (
          <div>
            <dt>Aprovado por</dt>
            <dd>
              {standing.approval.approved_by.display_name} em{' '}
              {formatDateTime(standing.approval.approved_at)}
            </dd>
          </div>
        )}
      </dl>

      {standing.is_overdue && (
        <p className="panel__footnote panel__footnote--warning">
          O corte de {formatDate(standing.cutoff_date)} já passou e este mês ainda não foi enviado.
        </p>
      )}

      {standing.last_failure != null && (
        <p className="panel__footnote panel__footnote--warning">
          Uma tentativa foi recusada em {formatDateTime(standing.last_failure.failed_at)}:{' '}
          {standing.last_failure.failure_reason}
        </p>
      )}

      {standing.stage === CycleStage.OPEN && (
        <p className="panel__footnote">{SUBMISSION_COPY.needsApproval}</p>
      )}

      {filedTogether && standing.group_id == null && (
        <>
          <p className="panel__footnote">{SUBMISSION_COPY.filedTogether}</p>
          <div className="form__actions">
            <Link
              className="button button--secondary"
              to={staffSubmissionConsolidatedPath(
                standing.company_id,
                standing.insurer_id,
                standing.cycle_month,
              )}
            >
              {SUBMISSION_COPY.openConsolidated}
            </Link>
          </div>
        </>
      )}

      {filedTogether && standing.group_id != null && (
        <>
          <p className="panel__footnote">{SUBMISSION_COPY.filedWithGroup}</p>
          <div className="form__actions">
            <Link
              className="button button--secondary"
              to={staffSubmissionGroupPath(
                standing.group_id,
                standing.insurer_id,
                standing.cycle_month,
              )}
            >
              {SUBMISSION_COPY.openGroup}
            </Link>
          </div>
        </>
      )}

      {canPrepare && (
        <>
          <p className="panel__footnote">{SUBMISSION_COPY.readyToPrepare}</p>
          <FormError error={prepare.error} />
          <div className="form__actions">
            <button
              type="button"
              className="button button--primary"
              disabled={prepare.isPending}
              onClick={() =>
                prepare.mutate(
                  {
                    ...target,
                    // The digest the sign-off pinned, so packaging anything else
                    // is a 409 rather than a surprise in a dispute later.
                    ...(standing.approval != null
                      ? { snapshotSha256: standing.approval.snapshot_sha256 }
                      : {}),
                  },
                  { onSuccess: () => notify('success', SUBMISSION_COPY.prepared) },
                )
              }
            >
              {SUBMISSION_COPY.prepare}
            </button>
          </div>
        </>
      )}

      {submission != null && <SubmissionEvidence submission={submission} />}

      {!filedTogether && isOpenAttempt(submission) && submission != null && (
        <>
          <p className="panel__footnote">{SUBMISSION_COPY.preparedFootnote}</p>
          {marking === null && (
            <div className="form__actions">
              <button
                type="button"
                className="button button--primary"
                onClick={() => setMarking('sent')}
              >
                {SUBMISSION_COPY.markSent}
              </button>
              <button
                type="button"
                className="button button--secondary"
                onClick={() => setMarking('failed')}
              >
                {SUBMISSION_COPY.markFailed}
              </button>
            </div>
          )}
          {marking === 'sent' && (
            <MarkSentForm
              pending={markSent.isPending}
              error={markSent.error}
              onCancel={() => setMarking(null)}
              onSubmit={(fields) =>
                markSent.mutate(
                  { submissionId: submission.id, companyId: submission.company_id, ...fields },
                  {
                    onSuccess: () => {
                      notify('success', SUBMISSION_COPY.sent);
                      setMarking(null);
                    },
                  },
                )
              }
            />
          )}
          {marking === 'failed' && (
            <MarkFailedForm
              pending={markFailed.isPending}
              error={markFailed.error}
              onCancel={() => setMarking(null)}
              onSubmit={(reason) =>
                markFailed.mutate(
                  { submissionId: submission.id, companyId: submission.company_id, reason },
                  {
                    onSuccess: () => {
                      notify('warning', SUBMISSION_COPY.failed);
                      setMarking(null);
                    },
                  },
                )
              }
            />
          )}
        </>
      )}

      {standing.stage === CycleStage.SENT && (
        <p className="panel__footnote">{SUBMISSION_COPY.sentFootnote}</p>
      )}

      <p className="panel__footnote">{SUBMISSION_COPY.noTransmission}</p>
    </Panel>
  );
}

/**
 * What is recorded about the attempt standing, including where to get the file.
 *
 * The file is linked rather than embedded: `GET /snapshots/{id}/file` is the one
 * authenticated path to a snapshot's bytes and it audits every read of one, so a
 * second way to reach them would be a second thing to keep tenancy-checked.
 *
 * The digest and the button sit on the same line because they are the same
 * claim: "the file I am holding is the one the sign-off pinned" is checked by
 * hashing what came out of the download against what the approval recorded.
 * The download keeps the name the server stored the snapshot under, so what is
 * on an analyst's disk is what the insurer was sent, spelled the same way.
 */
function SubmissionEvidence({ submission }: { submission: SubmissionResponse }) {
  const proof = proofOf(submission);
  const file = useSnapshotFile();
  const { notify } = useNotifications();

  return (
    <dl className="definitions">
      {submission.snapshot_sha256 != null && (
        <div>
          <dt>Arquivo</dt>
          <dd>
            <code>{submission.snapshot_sha256}</code>
          </dd>
        </div>
      )}
      {submission.snapshot_id != null && (
        <div className="form__actions">
          <button
            type="button"
            className="button button--secondary"
            disabled={file.isPending}
            onClick={() =>
              file.mutate(
                { companyId: submission.company_id, snapshotId: submission.snapshot_id ?? '' },
                {
                  // The month is the fallback and not the name: it is what a
                  // person would call the file if the server, against its own
                  // handler, declined to.
                  onSuccess: ({ blob, filename }) =>
                    saveAsAttachment(
                      blob,
                      filename ??
                        `envio-${submission.cycle_month ?? submission.covers_through}.csv`,
                    ),
                  onError: () => notify('error', SUBMISSION_COPY.downloadFailed),
                },
              )
            }
          >
            {file.isPending ? SUBMISSION_COPY.downloading : SUBMISSION_COPY.downloadFile}
          </button>
        </div>
      )}
      {submission.prepared_by != null && (
        <div>
          <dt>Gerado por</dt>
          <dd>
            {submission.prepared_by.display_name} em {formatDateTime(submission.prepared_at)}
          </dd>
        </div>
      )}
      {submission.status === SubmissionStatus.SENT && (
        <>
          <div>
            <dt>Enviado por</dt>
            <dd>
              {submission.sent_by?.display_name ?? '—'} em {formatDateTime(submission.submitted_at)}
            </dd>
          </div>
          <div>
            <dt>Por onde</dt>
            <dd>{submission.channel != null ? CHANNEL_LABELS[submission.channel] : '—'}</dd>
          </div>
          <div>
            <dt>Comprovação</dt>
            {/* Never empty on a sent attempt -- the server refuses the marker
                without one -- but rendered from the data rather than asserted,
                so a month that somehow had none would say so loudly. */}
            <dd>{proof.length > 0 ? proof.join(' · ') : 'Nada registrado'}</dd>
          </div>
        </>
      )}
    </dl>
  );
}
